import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Loader2,
  Zap,
  ShoppingBag,
  Star,
  TrendingUp,
  Sparkles,
  Package
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Categories for the carousel - STRICT ORDER from Right to Left
const categories = [
  { id: 'all', name: 'جميع العروض', icon: Sparkles, color: 'bg-gradient-to-l from-purple-500 to-purple-600' },
  { id: 'amazon', name: 'أمازون', icon: Package, color: 'bg-gradient-to-l from-amber-500 to-amber-600' },
  { id: 'noon', name: 'نون', icon: Star, color: 'bg-gradient-to-l from-blue-500 to-blue-600' },
  { id: 'namshi', name: 'نمشي', icon: ShoppingBag, color: 'bg-gradient-to-l from-pink-500 to-pink-600' },
  { id: 'aliexpress', name: 'علي إكسبريس', icon: TrendingUp, color: 'bg-gradient-to-l from-green-500 to-green-600' },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [banner, setBanner] = useState(null);
  const LIMIT = 12;

  useEffect(() => {
    fetchBanner();
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchProducts(selectedCategory, debouncedSearchTerm);
  }, [selectedCategory, debouncedSearchTerm]);

  const fetchBanner = async () => {
    try {
      const response = await axios.get(`${API}/settings/banner`, { params: { key: 'home' } });
      setBanner(response.data);
    } catch (error) {
      console.error('خطأ في تحميل البنر:', error);
    }
  };

  const fetchProducts = async (category = 'all', term = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', LIMIT);
      params.set('skip', 0);
      if (category !== 'all') {
        params.set('category', category);
      }
      if (term) {
        params.set('q', term);
      }
      const url = `${API}/products?${params.toString()}`;

      const countParams = new URLSearchParams();
      if (category !== 'all') {
        countParams.set('category', category);
      }
      if (term) {
        countParams.set('q', term);
      }
      const countUrl = `${API}/products/count/total${countParams.toString() ? `?${countParams.toString()}` : ''}`;
      
      const [productsRes, countRes] = await Promise.all([
        axios.get(url),
        axios.get(countUrl)
      ]);
      
      setProducts(productsRes.data);
      setHasMore(productsRes.data.length < countRes.data.count);
      setSkip(LIMIT);
    } catch (error) {
      console.error('خطأ في تحميل المنتجات:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', LIMIT);
      params.set('skip', skip);
      if (selectedCategory !== 'all') {
        params.set('category', selectedCategory);
      }
      if (debouncedSearchTerm) {
        params.set('q', debouncedSearchTerm);
      }
      const url = `${API}/products?${params.toString()}`;

      const countParams = new URLSearchParams();
      if (selectedCategory !== 'all') {
        countParams.set('category', selectedCategory);
      }
      if (debouncedSearchTerm) {
        countParams.set('q', debouncedSearchTerm);
      }
      const countUrl = `${API}/products/count/total${countParams.toString() ? `?${countParams.toString()}` : ''}`;
      
      const [productsRes, countRes] = await Promise.all([
        axios.get(url),
        axios.get(countUrl)
      ]);

      setProducts(prev => {
        const updated = [...prev, ...productsRes.data];
        setHasMore(updated.length < countRes.data.count);
        return updated;
      });
      setSkip(prev => prev + LIMIT);
    } catch (error) {
      console.error('خطأ في تحميل المزيد:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const resolveBannerUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL || ''}${url}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50" dir="rtl">
      <Helmet>
        <title>صفقة - أفضل العروض والصفقات من أمازون ونون ونمشي وعلي إكسبريس</title>
        <meta name="description" content="اكتشف أفضل العروض والخصومات من أمازون، نون، نمشي، وعلي إكسبريس. منتجات مميزة بأسعار رائعة وأكواد خصم حصرية." />
        <meta name="keywords" content="عروض, خصومات, أمازون, نون, نمشي, علي إكسبريس, كوبونات, تسوق أونلاين, صفقات" />
        <meta property="og:title" content="صفقة - أفضل العروض والصفقات" />
        <meta property="og:description" content="اكتشف أفضل العروض والخصومات من أمازون، نون، نمشي، وعلي إكسبريس." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="صفقة - أفضل العروض والصفقات" />
        <meta name="twitter:description" content="اكتشف أفضل العروض والخصومات من أمازون، نون، نمشي، وعلي إكسبريس." />
        <link rel="canonical" href={window.location.origin} />
      </Helmet>

      <Header searchValue={searchTerm} onSearchChange={setSearchTerm} />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="bg-gradient-to-l from-primary/10 to-primary/5 py-8 md:py-12">
          <div className="container mx-auto px-4">
            <div className="text-center space-y-4 max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight">
                اكتشف <span className="text-primary">أفضل الصفقات</span> من المتاجر الرائدة
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                نوفر لك أحدث العروض والخصومات من أمازون، نون، نمشي، وعلي إكسبريس بأسعار لا تُقاوم
              </p>
            </div>
          </div>
        </div>

        {/* Categories Carousel - Centered */}
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="mb-6 flex flex-col items-center justify-center">
            <h2 className="text-xl font-bold text-gray-900 mb-4 text-right">تصفح حسب التصنيف</h2>
            <div className="w-full flex justify-center">
              <div className="w-full max-w-4xl flex justify-center">
                <Carousel
                  opts={{
                    align: "center",
                    dragFree: true,
                    direction: 'rtl',
                  }}
                  className="w-full flex justify-center"
                >
                  <CarouselContent className="flex items-center justify-center">
                    {categories.map((category) => {
                      const Icon = category.icon;
                      return (
                        <CarouselItem key={category.id} className="flex-shrink-0 basis-auto flex justify-center">
                          <Button
                            variant={selectedCategory === category.id ? "default" : "outline"}
                            className={`h-auto py-3 px-4 rounded-xl transition-all duration-300 mx-1 ${
                              selectedCategory === category.id 
                                ? `${category.color} text-white border-0 shadow-md` 
                                : 'bg-white hover:bg-gray-50 border-gray-200'
                            }`}
                            onClick={() => setSelectedCategory(category.id === 'all' ? 'all' : category.id)}
                          >
                            <div className="flex flex-col items-center gap-2">
                              <Icon className={`h-5 w-5 ${selectedCategory === category.id ? 'text-white' : 'text-gray-600'}`} />
                              <span className="text-sm font-medium whitespace-nowrap">{category.name}</span>
                            </div>
                          </Button>
                        </CarouselItem>
                      );
                    })}
                  </CarouselContent>
                  <CarouselPrevious className="hidden md:flex -left-4" />
                  <CarouselNext className="hidden md:flex -right-4" />
                </Carousel>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 text-right">
                {selectedCategory === 'all' ? 'أحدث العروض' : `عروض ${categories.find(c => c.id === selectedCategory)?.name}`}
              </h2>
              <p className="text-gray-600 text-right mt-1">
                {products.length} منتج متاح
              </p>
            </div>
            <Badge variant="outline" className="text-sm">
              <Zap className="ml-1 h-3 w-3" />
              تحديث لحظي
            </Badge>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-4">
              <Loader2 className="h-16 w-16 animate-spin text-primary" />
              <p className="text-lg text-gray-600">جاري تحميل العروض...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20 space-y-4">
              <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
                <ShoppingBag className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">لا توجد عروض حالياً</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                لم نعثر على عروض في هذا التصنيف. جرب تصنيفاً آخر أو عد لاحقاً.
              </p>
            </div>
          ) : (
            <>
              {/* Products Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Load More Button */}
              {hasMore && (
                <div className="flex justify-center mt-12">
                  <Button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="bg-gradient-to-l from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white font-bold px-10 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                    data-testid="load-more-button"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="ml-2 h-5 w-5 animate-spin" />
                        جارٍ التحميل...
                      </>
                    ) : (
                      <>
                        <Zap className="ml-2 h-5 w-5" />
                        تحميل المزيد من العروض
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Promo Banner */}
        {!loading && products.length > 0 && banner?.image_url && banner?.is_active !== false && (
          <div className="container mx-auto px-4 py-8">
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
              {banner?.link_url ? (
                <a href={banner.link_url} className="block" rel="noopener noreferrer" target="_blank">
                  <img
                    src={resolveBannerUrl(banner.image_url)}
                    alt="بنر العروض"
                    className="w-full h-auto object-cover"
                  />
                </a>
              ) : (
                <img
                  src={resolveBannerUrl(banner.image_url)}
                  alt="بنر العروض"
                  className="w-full h-auto object-cover"
                />
              )}
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
